import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Heart, Eye, X } from "lucide-react";
import InfinityCaseDetail from "../../components/admin/InfinityCaseDetail";
import Pagination from "../../components/shared/Pagination";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const PRINT_LABEL = { fingerprint: "Fingerprint", handfoot: "Hand & Foot", nose: "Nose", paw: "Paw", picture: "Photo", signature: "Signature" };
const PRINT_TYPES = ["fingerprint", "handfoot", "paw", "nose", "signature", "picture"];
const ALL = "all"; // sentinel for "no filter" (Radix Select disallows empty value)

export default function AdminInfinityCases() {
  const [records, setRecords] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [caseId, setCaseId] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(ALL);       // all | saved | ordered
  const [storeId, setStoreId] = useState(ALL);      // all | <store_id>
  const [printType, setPrintType] = useState(ALL);  // all | <print_type>
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Pagination (server-side)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchRecords = async (params) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/memorial-records`, { params });
      const data = res.data?.data || [];
      setRecords(data);
      const pg = res.data?.pagination;
      setTotalItems(pg?.total ?? data.length);
      setTotalPages(pg?.pages ?? 1);
    } catch (e) {
      toast.error("Failed to load cases");
    } finally {
      setLoading(false);
    }
  };

  // Load the funeral-home list once for the store filter dropdown.
  useEffect(() => {
    axios.get(`${API}/admin/stores`)
      .then((res) => setStores(Array.isArray(res.data) ? res.data : (res.data?.data || [])))
      .catch(() => {});
  }, []);

  // A narrowed result set almost never has the page you were on, so go back to 1.
  useEffect(() => { setPage(1); }, [search, status, storeId, printType, dateFrom, dateTo, pageSize]);

  // Re-query whenever any filter changes (search debounced together with the rest).
  useEffect(() => {
    const t = setTimeout(() => {
      const params = { page, limit: pageSize };
      const q = search.trim();
      if (q) params.search = q;
      if (status !== ALL) params.status = status;
      if (storeId !== ALL) params.store_id = storeId;
      if (printType !== ALL) params.print_type = printType;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      fetchRecords(params);
    }, 350);
    return () => clearTimeout(t);
  }, [search, status, storeId, printType, dateFrom, dateTo, page, pageSize]);

  const hasFilters = search || status !== ALL || storeId !== ALL || printType !== ALL || dateFrom || dateTo;
  const clearFilters = () => { setSearch(""); setStatus(ALL); setStoreId(ALL); setPrintType(ALL); setDateFrom(""); setDateTo(""); };

  const subjectName = (s) => [s?.first_name, s?.last_name].filter(Boolean).join(" ") || "—";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-infinity-cases">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Heart className="w-6 h-6 text-rose-500" /> Infinity Touch Cases</h2>
        <p className="text-slate-500">Memorial print records (cases) across all funeral homes.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search by case #, first or last name" value={search} onChange={(e) => setSearch(e.target.value)} className="w-60" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            <SelectItem value="saved">Saved Print</SelectItem>
            <SelectItem value="ordered">Print Order</SelectItem>
          </SelectContent>
        </Select>
        <Select value={storeId} onValueChange={setStoreId}>
          <SelectTrigger className="w-52"><SelectValue placeholder="Funeral home" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All funeral homes</SelectItem>
            {stores.map((s) => (
              <SelectItem key={s.store_id} value={s.store_id}>{s.business_name || s.subdomain || s.store_id}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={printType} onValueChange={setPrintType}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Print type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All print types</SelectItem>
            {PRINT_TYPES.map((pt) => <SelectItem key={pt} value={pt}>{PRINT_LABEL[pt]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" aria-label="From date" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" aria-label="To date" />
        {hasFilters && (
          <Button variant="ghost" size="sm" className="text-slate-500" onClick={clearFilters}>
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      <div className="card-elevated overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">Case #</TableHead>
              <TableHead className="font-semibold">Deceased / Pet</TableHead>
              <TableHead className="font-semibold">Funeral Home</TableHead>
              <TableHead className="font-semibold">Print Type</TableHead>
              <TableHead className="font-semibold">Captures</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">Created</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">Loading…</TableCell></TableRow>
            ) : records.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">No cases found.</TableCell></TableRow>
            ) : records.map((r) => (
              <TableRow key={r.record_id} className="table-row cursor-pointer" onClick={() => setCaseId(r.record_id)}>
                <TableCell className="font-medium">{r.case_number}</TableCell>
                <TableCell>{subjectName(r.subject)}</TableCell>
                <TableCell>{r.store_name || r.store_subdomain || r.store_id}</TableCell>
                <TableCell>{PRINT_LABEL[r.print_type] || r.print_type}</TableCell>
                <TableCell>{(r.captures || []).length}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={r.status === "ordered" ? "border-green-200 bg-green-50 text-green-700" : "border-slate-200 bg-slate-50 text-slate-600"}>
                    {r.status === "ordered" ? "Ordered" : "Saved"}
                  </Badge>
                </TableCell>
                <TableCell>{fmtDate(r.created_at)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" className="text-slate-600"
                    onClick={(e) => { e.stopPropagation(); setCaseId(r.record_id); }}>
                    <Eye className="w-4 h-4 mr-1" /> View details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!loading && totalItems > 0 && (
          <div className="px-4 py-4 border-t">
            <Pagination page={page} totalPages={totalPages} onChange={setPage}
              totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} />
          </div>
        )}
      </div>

      {caseId && (
        <InfinityCaseDetail open={!!caseId} onClose={() => setCaseId(null)} memorialRecordId={caseId} />
      )}
    </div>
  );
}
